import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useAddEquipmentInspection,
  useDeleteEquipment,
  useEquipment,
  useEquipmentCategories,
  useEquipmentInspections,
  useEquipmentLoans,
  useEquipmentSuggestions,
  useEquipmentSummary,
  useHandOutEquipment,
  useProjects,
  useReturnEquipment,
  useSaveEquipment,
  useUsers
} from '../api/queries'
import type { Equipment, EquipmentInput, EquipmentStatus } from '../api/types'
import { projectOptionLabel } from '../lib/projectLabel'
import { useAuth } from '../auth/AuthProvider'
import { Avatar, Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { describeReservation, EquipmentPhoto, EquipmentStatusBadge } from '../components/EquipmentBits'

function today() {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

function formatDate(value?: string | null) {
  if (!value) return '-'
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Hele sætningen, så "Ude i" + "i dag" ikke bliver til "Ude i i dag". */
function describeTimeOut(days: number) {
  if (days === 0) return 'Udleveret i dag'
  if (days === 1) return 'Ude i 1 dag'
  if (days < 60) return `Ude i ${days} dage`
  return `Ude i ${Math.round(days / 30)} måneder`
}

function describeSpan(days: number) {
  if (days === 0) return 'samme dag'
  if (days === 1) return '1 dag'
  if (days < 60) return `${days} dage`
  return `${Math.round(days / 30)} måneder`
}

/** "Eftersyn om 14 dage", "Eftersyn overskredet med 5 dage", "Efterses ikke". */
function describeInspection(days?: number | null) {
  if (days === null || days === undefined) return 'Efterses ikke'
  if (days < 0) {
    const n = Math.abs(days)
    return `Eftersyn overskredet med ${n} ${n === 1 ? 'dag' : 'dage'}`
  }
  if (days === 0) return 'Eftersyn forfalder i dag'
  if (days === 1) return 'Eftersyn i morgen'
  if (days < 60) return `Eftersyn om ${days} dage`
  return `Eftersyn om ${Math.round(days / 30)} måneder`
}

/**
 * Farven på stregen ude i venstre side.
 *
 * Rød når eftersynet er overskredet, gul når det trænger eller grejet er ude.
 * Uden den gule på et forfaldent eftersyn ville advarslen øverst sige, at noget
 * skal efterses, uden at man kunne se hvilket.
 */
function stripeFor(item: Equipment, warningDays: number) {
  if (item.isInspectionOverdue) return '#DC2626'

  const days = item.daysUntilInspection
  if (days !== null && days !== undefined && days <= warningDays) return '#D97706'

  return item.currentLoan ? '#D97706' : 'transparent'
}

/**
 * Værktøj på udlån (beslutning 057, udvidet i 096).
 *
 * To spørgsmål på én side: hvem har grejet, og hvornår skal det efterses.
 * Det med overskredet eftersyn står øverst - stigen er ude i en bil, og det er
 * netop dét, der gør, at eftersynet bliver glemt.
 *
 * Systemet spærrer ikke: grej med overskredet eftersyn kan stadig udleveres, men
 * det står i loggen, og både låner og administratorer får besked.
 */
export function EquipmentPage() {
  const { user, isAdmin, isProjectManager } = useAuth()
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState(0)
  const [status, setStatus] = useState<EquipmentStatus | ''>('')
  const [outOnly, setOutOnly] = useState(false)
  const [dueOnly, setDueOnly] = useState(false)
  const [includeInactive, setIncludeInactive] = useState(false)
  const [editing, setEditing] = useState<Equipment | null | undefined>(undefined)
  const [handingOut, setHandingOut] = useState<Equipment | null>(null)
  const [inspecting, setInspecting] = useState<Equipment | null>(null)
  const [history, setHistory] = useState<Equipment | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const { data, isLoading, error } = useEquipment({
    search,
    categoryId: categoryId || undefined,
    status: status || undefined,
    outOnly,
    dueOnly,
    includeInactive
  })
  const { data: summary } = useEquipmentSummary()
  const { data: categories } = useEquipmentCategories()
  const remove = useDeleteEquipment()
  const returnItem = useReturnEquipment()
  const handOut = useHandOutEquipment()

  /**
   * "Tag den" - montøren tager grejet selv (beslutning 096). Står man med
   * mærkatet i hånden, giver det ingen mening at skulle finde en projektleder.
   */
  async function handleTake(item: Equipment) {
    setActionError(null)
    if (!confirm(`Tag "${item.name}" med? Så står den på dig, indtil du afleverer den.`)) return

    try {
      await handOut.mutateAsync({ id: item.id })
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Grejet kunne ikke tages.')
    }
  }

  async function handleReturn(item: Equipment) {
    setActionError(null)
    const holder = item.currentLoan?.user.displayName ?? 'nogen'
    if (!confirm(`Registrér "${item.name}" som afleveret af ${holder}?`)) return

    try {
      await returnItem.mutateAsync({ id: item.id, note: null })
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Grejet kunne ikke registreres som afleveret.')
    }
  }

  async function handleDelete(item: Equipment) {
    setActionError(null)
    if (
      !confirm(
        `Slet "${item.name}" og hele dets historik?\n\n` +
          'Er grejet kasseret, så fjern i stedet fluebenet "Grejet er i brug" - så bliver historikken stående.'
      )
    ) {
      return
    }

    try {
      await remove.mutateAsync(item.id)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Grejet kunne ikke slettes.')
    }
  }

  const canReturn = (item: Equipment) =>
    item.currentLoan != null && (isProjectManager || item.currentLoan.user.id === user?.id)

  return (
    <>
      <div className="cal-toolbar">
        <div className="cal-title">
          {summary ? `${summary.out} af ${summary.total} ude` : `${data?.length ?? 0} stykker`}
        </div>

        <input
          placeholder="Søg i navn, type, mærkning, serienummer eller kode ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />

        <select
          value={categoryId}
          onChange={(e) => setCategoryId(Number(e.target.value))}
          style={{ maxWidth: 200 }}
        >
          <option value={0}>Alle kategorier</option>
          {(categories ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.icon ? `${c.icon} ` : ''}
              {c.name}
            </option>
          ))}
        </select>

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as EquipmentStatus | '')}
          style={{ maxWidth: 170 }}
        >
          <option value="">Alle tilstande</option>
          <option value="ok">I orden</option>
          <option value="service">Til service</option>
          <option value="defect">Defekt</option>
        </select>

        <label className="cal-person" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={outOnly} onChange={(e) => setOutOnly(e.target.checked)} />
          Kun det der er ude
        </label>

        <label className="cal-person" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={dueOnly} onChange={(e) => setDueOnly(e.target.checked)} />
          Kun eftersyn der trænger
        </label>

        <label className="cal-person" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Vis kasseret
        </label>

        {isAdmin && (
          <button className="btn btn--primary btn--sm" onClick={() => setEditing(null)}>
            Opret værktøj
          </button>
        )}
      </div>

      {summary && (summary.inspectionOverdue > 0 || summary.inspectionDue > 0) && (
        <div
          className="banner"
          style={{
            marginBottom: 12,
            borderLeft: `4px solid ${summary.inspectionOverdue > 0 ? '#DC2626' : '#D97706'}`
          }}
        >
          {summary.inspectionOverdue > 0 && (
            <>
              <strong>
                {summary.inspectionOverdue}{' '}
                {summary.inspectionOverdue === 1 ? 'stykke grej har' : 'stykker grej har'} overskredet
                eftersyn.
              </strong>{' '}
              Systemet spærrer ikke - men om det må bruges, er en vurdering på stedet.{' '}
            </>
          )}
          {summary.inspectionDue > 0 && (
            <>
              {summary.inspectionDue} skal efterses inden for {summary.warningDays} dage. Er grejet ude,
              skal det hjem først.
            </>
          )}
        </div>
      )}

      {summary && summary.needsAttention > 0 && (
        <div className="banner" style={{ marginBottom: 12, borderLeft: '4px solid #DC2626' }}>
          <strong>
            {summary.needsAttention}{' '}
            {summary.needsAttention === 1 ? 'stykke værktøj er' : 'stykker værktøj er'} til service
            eller defekt.
          </strong>{' '}
          Det kan hverken tages med eller reserveres, før tilstanden er sat til i orden igen.
        </div>
      )}

      {actionError && <ErrorBox error={actionError} />}
      {error && <ErrorBox error={error} />}
      {isLoading && <Loading />}

      {data && data.length === 0 && (
        <EmptyState>
          {search || outOnly || dueOnly
            ? 'Intet værktøj passer på det, du søger efter.'
            : isAdmin
              ? 'Der står intet værktøj endnu. Tryk Opret værktøj - én række pr. stykke grej, så man kan se, hvem der har hvilket.'
              : 'Der står intet værktøj endnu. En administrator kan skrive det ind.'}
        </EmptyState>
      )}

      {data && data.length > 0 && (
        <Card>
          <div className="stack" style={{ gap: 8 }}>
            {data.map((item) => (
              <div
                key={item.id}
                className="row"
                style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap', opacity: item.isActive ? 1 : 0.55 }}
              >
                <span
                  style={{
                    width: 6,
                    alignSelf: 'stretch',
                    minHeight: 44,
                    borderRadius: 3,
                    background: stripeFor(item, summary?.warningDays ?? 21),
                    flexShrink: 0
                  }}
                />

                <Link to={`/equipment/${item.id}`} title={`Åbn ${item.name}`}>
                  <EquipmentPhoto item={item} size={46} />
                </Link>

                <div style={{ minWidth: 190, flex: 1 }}>
                  <div className="row" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Link to={`/equipment/${item.id}`} style={{ fontWeight: 600 }}>
                      {item.name}
                    </Link>
                    <EquipmentStatusBadge status={item.status} label={item.statusLabel} />
                    {item.identifier && <span className="tiny muted">· {item.identifier}</span>}
                    {!item.isActive && <span className="tiny muted">· kasseret</span>}
                  </div>
                  <div className="small muted">
                    {item.categoryName ?? item.equipmentType ?? 'Værktøj'}
                    {item.categoryName && item.equipmentType && ` · ${item.equipmentType}`}
                    {item.manufacturer && ` · ${item.manufacturer}`}
                    {item.serialNumber && ` · nr. ${item.serialNumber}`}
                  </div>
                  <div
                    className="tiny"
                    style={{
                      color: item.isInspectionOverdue ? '#DC2626' : undefined,
                      fontWeight: item.isInspectionOverdue ? 600 : 400
                    }}
                  >
                    {describeInspection(item.daysUntilInspection)}
                    {item.nextInspectionOn && ` · ${formatDate(item.nextInspectionOn)}`}
                  </div>
                </div>

                <div style={{ minWidth: 195 }}>
                  {item.currentLoan ? (
                    <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                      <Avatar user={item.currentLoan.user} size={26} />
                      <div>
                        <div className="small" style={{ fontWeight: 600 }}>
                          {item.currentLoan.user.displayName}
                        </div>
                        <div className="tiny muted">
                          {describeTimeOut(item.currentLoan.daysOut)}
                          {item.currentLoan.expectedReturnOn &&
                            ` · retur ${formatDate(item.currentLoan.expectedReturnOn)}`}
                          {item.currentLoan.projectName && ` · ${item.currentLoan.projectName}`}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="small muted">
                      {item.storedAt ? `Ligger i ${item.storedAt}` : 'Ikke udleveret'}
                    </div>
                  )}

                  {describeReservation(item) && (
                    <div className="tiny muted" style={{ marginTop: 2 }}>
                      📅 {describeReservation(item)}
                      {item.openReservations > 1 && ` · ${item.openReservations} i kø`}
                    </div>
                  )}
                </div>

                <div className="row" style={{ gap: 6 }}>
                  {/* Foto, QR-maerkat og servicelog ligger paa vaerktoejets egen side.
                      Navnet er ogsaa et link derind, men det kunne ingen se - saa
                      knappen staar her, og den staar foerst. */}
                  <Link className="btn btn--primary btn--sm" to={`/equipment/${item.id}`}>
                    Åbn
                  </Link>

                  <button className="btn btn--sm" onClick={() => setHistory(item)}>
                    Historik
                  </button>

                  {item.currentLoan ? (
                    canReturn(item) && (
                      <button className="btn btn--sm" onClick={() => void handleReturn(item)}>
                        Afleveret
                      </button>
                    )
                  ) : (
                    <>
                      {item.isUsable && (
                        <button
                          className="btn btn--sm"
                          disabled={handOut.isPending}
                          onClick={() => void handleTake(item)}
                        >
                          Tag den
                        </button>
                      )}
                      {isProjectManager && item.isUsable && (
                        <button className="btn btn--sm" onClick={() => setHandingOut(item)}>
                          Udlevér
                        </button>
                      )}
                    </>
                  )}

                  {isAdmin && (
                    <>
                      {item.inspectionIntervalMonths && (
                        <button className="btn btn--sm" onClick={() => setInspecting(item)}>
                          Eftersyn
                        </button>
                      )}
                      <button className="btn btn--sm" onClick={() => setEditing(item)}>
                        Ret
                      </button>
                      <button className="btn btn--danger btn--sm" onClick={() => void handleDelete(item)}>
                        Slet
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {editing !== undefined && (
        <EquipmentDialog equipment={editing} onClose={() => setEditing(undefined)} />
      )}
      {handingOut && <HandOutDialog equipment={handingOut} onClose={() => setHandingOut(null)} />}
      {inspecting && <InspectionDialog equipment={inspecting} onClose={() => setInspecting(null)} />}
      {history && <HistoryDialog equipment={history} onClose={() => setHistory(null)} />}
    </>
  )
}

function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal__card">{children}</div>
    </div>
  )
}

function HandOutDialog({ equipment, onClose }: { equipment: Equipment; onClose: () => void }) {
  const { data: users } = useUsers()
  // Projektlisten kommer sideopdelt fra API-et.
  const { data: projects } = useProjects()
  const handOut = useHandOutEquipment()
  const [userId, setUserId] = useState(0)
  const [projectId, setProjectId] = useState(0)
  const [expectedDays, setExpectedDays] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await handOut.mutateAsync({
        id: equipment.id,
        userId,
        expectedDays: expectedDays ? Number(expectedDays) : null,
        projectId: projectId || null,
        note: note.trim() || null
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Grejet kunne ikke udleveres.')
    }
  }

  return (
    <Modal onClose={onClose}>
      <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>Udlevér {equipment.name}</h2>
      <p className="small muted" style={{ marginTop: 0 }}>
        {equipment.equipmentType ?? 'Værktøj'}
        {equipment.identifier && ` · ${equipment.identifier}`}
      </p>

      {equipment.isInspectionOverdue && (
        <p className="small" style={{ marginTop: 0, color: '#DC2626' }}>
          <strong>Eftersynet er overskredet.</strong> Grejet kan godt udleveres - men om det må
          bruges, er en vurdering, du selv skal tage. Det bliver skrevet i loggen.
        </p>
      )}

      <form onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">Hvem får det</span>
          <select value={userId} required onChange={(e) => setUserId(Number(e.target.value))}>
            <option value={0} disabled>
              Vælg medarbejder ...
            </option>
            {(users ?? []).filter((u) => u.isActive).map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">Hvor mange dage regner I med (valgfri)</span>
          <input
            type="number"
            min={1}
            max={365}
            value={expectedDays}
            placeholder="F.eks. 3"
            onChange={(e) => setExpectedDays(e.target.value)}
          />
          <span className="tiny muted">
            Et skøn. Det spærrer ikke - det gør bare, at den næste kan se, hvornår grejet
            formentlig er fri igen.
          </span>
        </label>

        <label className="field">
          <span className="field__label">Til hvilken sag (valgfri)</span>
          <select value={projectId} onChange={(e) => setProjectId(Number(e.target.value))}>
            <option value={0}>Ingen bestemt sag</option>
            {(projects?.items ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {projectOptionLabel(p)}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">Note (valgfri)</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} />
        </label>

        {error && <ErrorBox error={error} />}

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button className="btn btn--primary" type="submit" disabled={handOut.isPending || !userId}>
            {handOut.isPending ? 'Gemmer ...' : 'Udlevér'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Annullér
          </button>
        </div>
      </form>
    </Modal>
  )
}

function InspectionDialog({ equipment, onClose }: { equipment: Equipment; onClose: () => void }) {
  const add = useAddEquipmentInspection()
  const [performedOn, setPerformedOn] = useState(today())
  const [performedBy, setPerformedBy] = useState('')
  const [isApproved, setIsApproved] = useState(true)
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await add.mutateAsync({
        id: equipment.id,
        // Eftersynet - det, der rykker naeste eftersynsdato. Service skrives paa
        // vaerktoejets egen side og maa alle skrive (beslutning 096).
        kind: 'inspection',
        performedOn,
        performedBy: performedBy.trim() || null,
        isApproved,
        notes: notes.trim() || null
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Eftersynet kunne ikke gemmes.')
    }
  }

  return (
    <Modal onClose={onClose}>
      <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>Eftersyn af {equipment.name}</h2>
      <p className="small muted" style={{ marginTop: 0 }}>
        Næste eftersyn regnes ud af intervallet på {equipment.inspectionIntervalMonths} måneder.
      </p>

      <form onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">Udført</span>
          <input
            type="date"
            value={performedOn}
            max={today()}
            onChange={(e) => setPerformedOn(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Efterset af</span>
          <input
            value={performedBy}
            placeholder="Eget navn eller firma udefra"
            onChange={(e) => setPerformedBy(e.target.value)}
          />
        </label>

        <label className="cal-person" style={{ cursor: 'pointer', marginBottom: 10 }}>
          <input
            type="checkbox"
            checked={isApproved}
            onChange={(e) => setIsApproved(e.target.checked)}
          />
          Grejet var i orden
        </label>

        <label className="field">
          <span className="field__label">
            {isApproved ? 'Bemærkninger (valgfri)' : 'Hvad skal udbedres'}
          </span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        <p className="tiny muted" style={{ marginTop: 0 }}>
          Eftersynet tæller som udført, også når der var noget at udbedre - så næste dato rykker
          frem uanset hvad. Hvad der mangler, står i noten.
        </p>

        {error && <ErrorBox error={error} />}

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button className="btn btn--primary" type="submit" disabled={add.isPending}>
            {add.isPending ? 'Gemmer ...' : 'Gem eftersyn'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Annullér
          </button>
        </div>
      </form>
    </Modal>
  )
}

function HistoryDialog({ equipment, onClose }: { equipment: Equipment; onClose: () => void }) {
  const { data: loans, isLoading } = useEquipmentLoans(equipment.id)
  const { data: inspections } = useEquipmentInspections(equipment.id)

  return (
    <Modal onClose={onClose}>
      <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>{equipment.name}</h2>

      {isLoading && <Loading />}

      <h3 style={{ fontSize: 13, marginBottom: 6 }}>Udlån</h3>
      {loans && loans.length === 0 && <p className="small muted">Grejet har aldrig været udleveret.</p>}
      <div className="stack" style={{ gap: 8 }}>
        {(loans ?? []).map((l) => (
          <div key={l.id} className="row" style={{ gap: 8, alignItems: 'center' }}>
            <Avatar user={l.user} size={26} />
            <div style={{ flex: 1 }}>
              <div className="small" style={{ fontWeight: 600 }}>
                {l.user.displayName}
                {!l.returnedOn && <span style={{ color: '#D97706' }}> · har det nu</span>}
              </div>
              <div className="tiny muted">
                {formatDate(l.handedOutOn)} - {l.returnedOn ? formatDate(l.returnedOn) : 'ikke afleveret'} ·{' '}
                {describeSpan(l.daysOut)}
                {l.projectName && ` · ${l.projectName}`}
              </div>
              {l.note && <div className="tiny">{l.note}</div>}
            </div>
          </div>
        ))}
      </div>

      {inspections && inspections.length > 0 && (
        <>
          <h3 style={{ fontSize: 13, marginBottom: 6, marginTop: 16 }}>Service og eftersyn</h3>
          <p className="tiny muted" style={{ marginTop: 0 }}>
            Historikken slettes aldrig - efter en ulykke er spørgsmålet, om grejet var efterset
            dengang. Billederne står på værktøjets egen side.
          </p>
          <div className="stack" style={{ gap: 6 }}>
            {inspections.map((i) => (
              <div key={i.id}>
                <div className="small">
                  <strong>{i.kind === 'inspection' ? 'Eftersyn' : 'Service'}</strong>{' '}
                  {formatDate(i.performedOn)}
                  {i.registeredBy && ` · ${i.registeredBy.displayName}`}
                  {i.performedBy && ` · udført af ${i.performedBy}`}
                  {i.kind === 'inspection' && !i.isApproved && (
                    <span style={{ color: '#B45309' }}> · med bemærkninger</span>
                  )}
                  {i.photos.length > 0 && (
                    <span className="muted">
                      {' '}
                      · 📷 {i.photos.length}
                    </span>
                  )}
                </div>
                {i.workPerformed && <div className="tiny">{i.workPerformed}</div>}
                {i.partsReplaced && <div className="tiny">Skiftet: {i.partsReplaced}</div>}
                {i.notes && <div className="tiny muted">{i.notes}</div>}
              </div>
            ))}
          </div>
        </>
      )}

      <div className="row" style={{ gap: 8, marginTop: 12 }}>
        <button className="btn" onClick={onClose}>
          Luk
        </button>
      </div>
    </Modal>
  )
}

function EquipmentDialog({
  equipment,
  onClose
}: {
  equipment: Equipment | null
  onClose: () => void
}) {
  const { data: suggestions } = useEquipmentSuggestions()
  const { data: categories } = useEquipmentCategories()
  const save = useSaveEquipment()

  const [form, setForm] = useState<EquipmentInput>({
    name: equipment?.name ?? '',
    equipmentType: equipment?.equipmentType ?? '',
    categoryId: equipment?.categoryId ?? null,
    status: equipment?.status ?? 'ok',
    identifier: equipment?.identifier ?? '',
    manufacturer: equipment?.manufacturer ?? '',
    serialNumber: equipment?.serialNumber ?? '',
    storedAt: equipment?.storedAt ?? '',
    inspectionIntervalMonths: equipment?.inspectionIntervalMonths ?? null,
    lastInspectionOn: equipment?.lastInspectionOn ?? '',
    nextInspectionOn: equipment?.nextInspectionOn ?? '',
    notes: equipment?.notes ?? '',
    isActive: equipment?.isActive ?? true
  })
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await save.mutateAsync({
        id: equipment?.id,
        body: {
          ...form,
          equipmentType: form.equipmentType?.trim() || null,
          identifier: form.identifier?.trim() || null,
          manufacturer: form.manufacturer?.trim() || null,
          serialNumber: form.serialNumber?.trim() || null,
          storedAt: form.storedAt?.trim() || null,
          lastInspectionOn: form.lastInspectionOn || null,
          nextInspectionOn: form.nextInspectionOn || null,
          notes: form.notes?.trim() || null
        }
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Grejet kunne ikke gemmes.')
    }
  }

  return (
    <Modal onClose={onClose}>
      <h2 style={{ margin: '0 0 14px', fontSize: 18 }}>
        {equipment ? 'Ret værktøj' : 'Opret værktøj'}
      </h2>

      <form onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">Hvad er det</span>
          <input
            value={form.name}
            required
            placeholder="F.eks. Wienerstige 3 m"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </label>

        <div className="row" style={{ gap: 10 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Kategori</span>
            <select
              value={form.categoryId ?? 0}
              onChange={(e) => setForm({ ...form, categoryId: Number(e.target.value) || null })}
            >
              <option value={0}>Uden kategori</option>
              {(categories ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.icon ? `${c.icon} ` : ''}
                  {c.name}
                </option>
              ))}
            </select>
            <span className="tiny muted">Hvad slags værktøj det er. Listen styres under Værktøjskategorier.</span>
          </label>

          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Tilstand</span>
            <select
              value={form.status ?? 'ok'}
              onChange={(e) => setForm({ ...form, status: e.target.value as EquipmentStatus })}
            >
              <option value="ok">I orden</option>
              <option value="service">Til service</option>
              <option value="defect">Defekt</option>
            </select>
            <span className="tiny muted">
              Til service og defekt spærrer for, at grejet tages med eller reserveres.
            </span>
          </label>
        </div>

        <div className="row" style={{ gap: 10 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Type</span>
            <input
              list="equipment-suggestions"
              value={form.equipmentType ?? ''}
              placeholder="F.eks. Stige"
              onChange={(e) => setForm({ ...form, equipmentType: e.target.value })}
            />
            <datalist id="equipment-suggestions">
              {(suggestions ?? []).map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </label>

          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Mærkning</span>
            <input
              value={form.identifier ?? ''}
              placeholder="F.eks. ST-3"
              onChange={(e) => setForm({ ...form, identifier: e.target.value })}
            />
          </label>
        </div>

        <div className="row" style={{ gap: 10 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Fabrikat</span>
            <input
              value={form.manufacturer ?? ''}
              onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
            />
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Serienummer</span>
            <input
              value={form.serialNumber ?? ''}
              onChange={(e) => setForm({ ...form, serialNumber: e.target.value })}
            />
          </label>
        </div>

        <label className="field">
          <span className="field__label">Hvor det ligger</span>
          <input
            value={form.storedAt ?? ''}
            placeholder="F.eks. Lager, reol 4"
            onChange={(e) => setForm({ ...form, storedAt: e.target.value })}
          />
        </label>

        <div className="row" style={{ gap: 10 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Eftersyn hver (måneder)</span>
            <input
              type="number"
              min={1}
              max={120}
              value={form.inspectionIntervalMonths ?? ''}
              placeholder="Tom = efterses ikke"
              onChange={(e) =>
                setForm({
                  ...form,
                  inspectionIntervalMonths: e.target.value ? Number(e.target.value) : null
                })
              }
            />
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Sidst efterset</span>
            <input
              type="date"
              value={form.lastInspectionOn ?? ''}
              onChange={(e) => setForm({ ...form, lastInspectionOn: e.target.value })}
            />
          </label>
        </div>

        <p className="small muted" style={{ marginTop: 0 }}>
          {form.inspectionIntervalMonths
            ? 'Næste dato regnes ud af intervallet - eller skriv den selv nedenfor, hvis den står på et papir.'
            : 'Uden et interval varsles der aldrig. Brug det til grej, der ikke skal efterses - en boremaskine skal ikke, en stige skal.'}
        </p>

        {form.inspectionIntervalMonths ? (
          <label className="field">
            <span className="field__label">Næste eftersyn (valgfri)</span>
            <input
              type="date"
              value={form.nextInspectionOn ?? ''}
              onChange={(e) => setForm({ ...form, nextInspectionOn: e.target.value })}
            />
          </label>
        ) : null}

        <label className="field">
          <span className="field__label">Note</span>
          <input
            value={form.notes ?? ''}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </label>

        <label className="cal-person" style={{ cursor: 'pointer', marginBottom: 10 }}>
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          />
          Grejet er i brug
        </label>
        <p className="tiny muted" style={{ marginTop: 0 }}>
          Fjern fluebenet, når grejet er kasseret. Så bliver historikken stående, men det kan ikke
          udleveres igen.
        </p>

        {error && <ErrorBox error={error} />}

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Gemmer ...' : 'Gem'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Annullér
          </button>
        </div>
      </form>
    </Modal>
  )
}
